import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import {
  dashboardStatisticsModules,
  toFailedMetricCard,
  toLoadingMetricCard,
  toMetricCard
} from '../constants/dashboardStatisticsConfig'
import { getDateRangeParams, formatUpdatedTime } from '../utils/statisticsFormat'

function hasDashboardRole(roleKeys) {
  const keys = Array.isArray(roleKeys) ? roleKeys : []
  return keys.includes('FA-CITY-MANAGER') || keys.includes('REGIONAL_PARTNER')
}

function canViewModule(module, permissions, roleKeys) {
  if (hasDashboardRole(roleKeys)) return true
  if (!module.permission) return true
  const list = Array.isArray(permissions) ? permissions : []
  return list.includes('*:*:*') || list.includes(module.permission)
}

export function useDashboardStatistics() {
  const userStore = useUserStore()
  const selectedRange = ref('last7Days')
  const loading = ref(false)
  const lastUpdatedAt = ref('')
  const moduleState = ref({})

  const visibleModules = computed(() => {
    return dashboardStatisticsModules.filter(module => {
      if (userStore.hasPermission(module.permission)) return true
      return canViewModule(module, userStore.permissions, userStore.roleKeys)
    })
  })

  const hasPermission = computed(() => {
    return visibleModules.value.length > 0
  })

  const metricCards = computed(() => {
    return visibleModules.value.map(module => {
      return moduleState.value[module.key] || toLoadingMetricCard(module)
    })
  })

  const allVisibleFailed = computed(() => {
    return visibleModules.value.length > 0 && metricCards.value.every(item => item.error)
  })

  function resetForVisibleModules(isLoading = false) {
    moduleState.value = visibleModules.value.reduce((result, module) => {
      result[module.key] = isLoading ? toLoadingMetricCard(module) : toMetricCard(module, {})
      return result
    }, {})
  }

  async function loadStatistics() {
    if (!hasPermission.value) {
      moduleState.value = {}
      return
    }

    loading.value = true
    resetForVisibleModules(true)

    const params = getDateRangeParams(selectedRange.value)
    const modules = visibleModules.value
    const summaryResults = await Promise.allSettled(
      modules.map(module => module.summaryApi(params))
    )

    const nextState = {}
    summaryResults.forEach((result, index) => {
      const module = modules[index]
      nextState[module.key] = result.status === 'fulfilled'
        ? toMetricCard(module, result.value || {})
        : toFailedMetricCard(module)
    })

    moduleState.value = nextState
    lastUpdatedAt.value = formatUpdatedTime()
    loading.value = false
  }

  watch(
    () => userStore.permissions,
    () => {
      if (hasPermission.value) {
        loadStatistics()
      } else {
        moduleState.value = {}
      }
    },
    { deep: true }
  )

  return {
    selectedRange,
    loading,
    lastUpdatedAt,
    visibleModules,
    metricCards,
    hasPermission,
    allVisibleFailed,
    loadStatistics
  }
}
